import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { dodoService } from '../services/dodoService';

interface SubscriptionStore {
  isSubscribed: boolean;
  isTrialActive: boolean;
  trialEndsAt: string | null;
  status: string | null;
  dodoCustomerId: string | null;
  checkSubscriptionStatus: (userId: string) => Promise<void>;
  createSubscription: (productId: string, userId: string, email: string) => Promise<void>;
}

export const useSubscriptionStore = create<SubscriptionStore>((set) => ({
  isSubscribed: false,
  isTrialActive: false,
  trialEndsAt: null,
  status: null,
  dodoCustomerId: null,

  checkSubscriptionStatus: async (userId: string) => {
    try {
      const { data: subscriptions, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // If no subscription exists, user gets a trial
      if (!subscriptions || subscriptions.length === 0) {
        // Create a trial subscription for new users
        const trialEndsAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days
        
        const { error: insertError } = await supabase
          .from('subscriptions')
          .insert({
            user_id: userId,
            status: 'trialing',
            trial_ends_at: trialEndsAt.toISOString(),
          });

        if (!insertError) {
          set({
            isSubscribed: false,
            isTrialActive: true,
            trialEndsAt: trialEndsAt.toISOString(),
            status: 'trialing',
            dodoCustomerId: null
          });
        }
        return;
      }

      // Get the most recent subscription
      const subscription = subscriptions[0];
      console.log('Current subscription:', subscription);

      if (subscription) {
        // Determine subscription status
        const now = new Date();
        const trialEnd = subscription.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
        
        // Check if user has an active paid subscription
        const isActiveSubscription = subscription.status === 'active' && 
          subscription.dodo_subscription_id;
        
        // Check if trial is still active
        const isTrialActive = subscription.status === 'trialing' && 
          trialEnd && trialEnd > now;
          
        // For incomplete Dodo subscriptions that should be active
        const shouldBeActive = subscription.dodo_subscription_id && 
          subscription.dodo_customer_id && 
          subscription.status === 'incomplete';

        // If Dodo subscription exists but status is incomplete, treat as active
        // This handles the case where webhook didn't fire properly
        if (shouldBeActive) {
          console.log('Found incomplete Dodo subscription, treating as active');
          
          // Update the status in database
          await supabase
            .from('subscriptions')
            .update({ 
              status: 'active',
              updated_at: new Date().toISOString(),
              current_period_start: new Date().toISOString(),
              current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
            })
            .eq('id', subscription.id);

          set({
            isSubscribed: true,
            isTrialActive: false,
            trialEndsAt: subscription.trial_ends_at,
            status: 'active',
            dodoCustomerId: subscription.dodo_customer_id
          });
          return;
        }

        set({
          isSubscribed: isActiveSubscription,
          isTrialActive: isTrialActive || false,
          trialEndsAt: subscription.trial_ends_at,
          status: subscription.status,
          dodoCustomerId: subscription.dodo_customer_id
        });

        // If trial has expired, update the status
        if (!isTrialActive && subscription.status === 'trialing' && !subscription.dodo_subscription_id) {
          await supabase
            .from('subscriptions')
            .update({ status: 'inactive' })
            .eq('id', subscription.id);
          
          set({ status: 'inactive', isTrialActive: false, isSubscribed: false });
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      // Set default state on error - user can still use the app
      set({
        isSubscribed: false,
        isTrialActive: false,
        trialEndsAt: null,
        status: 'error',
        dodoCustomerId: null
      });
    }
  },

  createSubscription: async (productId: string, userId: string, email: string) => {
    try {
      await dodoService.redirectToCheckout(productId, userId, email);
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  },
}));